import { fetchMailAttachmentBlobCached } from "@/lib/cache/fetchMailAttachmentBlobCached";
import { downloadMailAttachment } from "@/lib/api/imap";
import { uploadCoffreFile, type CoffreListZone } from "@/lib/suite/coffreSuiteApi";
import type { MailAttachment } from "@/lib/types/mail";

export type MailAttachmentLocation = {
  accountId: number;
  folder: string;
  messageUid: number;
};

export type SaveAttachmentResult = { ok: boolean; error?: string };

export async function saveMailAttachmentToCoffre(
  location: MailAttachmentLocation,
  attachment: MailAttachment,
  folderPath: string,
  zone: CoffreListZone = "vault"
): Promise<SaveAttachmentResult> {
  try {
    const blob = await fetchMailAttachmentBlobCached({
      accountId: location.accountId,
      folder: location.folder,
      uid: location.messageUid,
      part: attachment.part,
      filename: attachment.filename,
    });
    const res = await uploadCoffreFile({
      file: blob,
      filename: attachment.filename,
      path: folderPath,
      zone,
    });
    if (!res.ok) {
      const data = res.data as { message?: string; error?: string };
      const locked =
        res.status === 403 &&
        (data.error === "coffre_locked" ||
          data.error === "coffre_session_expired" ||
          (data.message ?? "").toLowerCase().includes("déverrouill"));
      return {
        ok: false,
        error:
          data.message ??
          (locked
            ? "Le Coffre est fermé. Saisissez votre mot de passe maître pour enregistrer."
            : "Échec Coffre."),
      };
    }
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Enregistrement Coffre impossible.",
    };
  }
}

export async function downloadMailAttachmentToDisk(
  location: MailAttachmentLocation,
  attachment: MailAttachment
): Promise<SaveAttachmentResult> {
  try {
    await downloadMailAttachment({
      accountId: location.accountId,
      folder: location.folder,
      uid: location.messageUid,
      part: attachment.part,
      filename: attachment.filename,
    });
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Téléchargement impossible.",
    };
  }
}

export type BulkSaveAttachmentsResult = {
  ok: number;
  failed: number;
  skipped: number;
};

/** Toutes les PJ d’un message → disque (séquentiel — plusieurs fichiers navigateur). */
export async function downloadAllMailAttachments(
  location: MailAttachmentLocation,
  attachments: MailAttachment[],
  onProgress?: (done: number, total: number) => void
): Promise<BulkSaveAttachmentsResult> {
  const files = attachments.filter((f) => f.part && f.filename);
  let ok = 0;
  let failed = 0;
  for (let i = 0; i < files.length; i++) {
    onProgress?.(i, files.length);
    const result = await downloadMailAttachmentToDisk(location, files[i]);
    if (result.ok) ok += 1;
    else failed += 1;
  }
  onProgress?.(files.length, files.length);
  return { ok, failed, skipped: 0 };
}

/** Toutes les PJ d’un message → Coffre (même dossier destination). */
export async function saveAllMailAttachmentsToCoffre(
  location: MailAttachmentLocation,
  attachments: MailAttachment[],
  folderPath: string,
  zone: CoffreListZone = "vault",
  onProgress?: (done: number, total: number) => void
): Promise<BulkSaveAttachmentsResult> {
  const files = attachments.filter((f) => f.part && f.filename);
  let ok = 0;
  let failed = 0;
  for (let i = 0; i < files.length; i++) {
    onProgress?.(i, files.length);
    const result = await saveMailAttachmentToCoffre(location, files[i], folderPath, zone);
    if (result.ok) ok += 1;
    else failed += 1;
  }
  onProgress?.(files.length, files.length);
  return { ok, failed, skipped: 0 };
}

